import {
  ContractLinksRequestSchema,
  ENDPOINTS,
  SetContractLinkRequestSchema,
  type ContractLinksResponse,
  type SetContractLinkRequest,
  type SetContractLinkResponse,
} from '@clm/shared-types';

import { apiGet, apiPost } from './client';

/**
 * Fixing a contract's links after it was filed — CLM-120 (D-110-4 point 5;
 * FR-08, DR-02, DR-03; R-120-1, R-120-18, R-120-19).
 *
 * `fetchContractLinks` is `GET /v1/contracts/{contract_key}/links`
 * (`admin:configure`): the contract's links, what the action may do and why
 * not, and the candidates — the upload's own list — each with the links the POST
 * would accept and their effects, computed by the POST's planner. With
 * `targetContractKey` the candidates are that one contract (the dialog's *Find
 * by ID*, past the list's 100-row cap). The response is parsed against the
 * registry schema, whose refinements pin that no unseen row pointing at the
 * contract is named, so an off-contract body is a `ZodError`, never a list.
 *
 * `setContractLink` is `POST` on the same path: one audited act. The body is
 * validated `.strict()` before anything is sent — no UPN, no role, no scope —
 * and carries `seen`, posted back unchanged from the GET (the contract's half and
 * the chosen candidate's), so a link applies only from the state the Admin saw.
 * The key travels in the path only, encoded, so either key shape reaches the
 * API as written.
 *
 * NFR-06: no `scope` key is ever sent — scope is the API's, from the token.
 */

const LINKS = ENDPOINTS['contract-links'];
const SET = ENDPOINTS['set-contract-link'];

export async function fetchContractLinks(
  contractKey: string,
  targetContractKey?: string,
  signal?: AbortSignal,
): Promise<ContractLinksResponse> {
  const request = ContractLinksRequestSchema.parse({
    contract_key: contractKey,
    ...(targetContractKey === undefined ? {} : { target_contract_key: targetContractKey }),
  });
  const path = LINKS.path.replace('{contract_key}', encodeURIComponent(request.contract_key));
  const raw = await apiGet<unknown>(path, {
    ...(request.target_contract_key === undefined ? {} : { query: { target_contract_key: request.target_contract_key } }),
    ...(signal === undefined ? {} : { signal }),
  });
  return LINKS.response.parse(raw);
}

export async function setContractLink(request: SetContractLinkRequest, signal?: AbortSignal): Promise<SetContractLinkResponse> {
  const body = SetContractLinkRequestSchema.parse(request);
  const { contract_key, ...rest } = body;
  const path = SET.path.replace('{contract_key}', encodeURIComponent(contract_key));
  const raw = await apiPost<unknown>(path, rest, signal === undefined ? {} : { signal });
  return SET.response.parse(raw);
}
